import React, { InputHTMLAttributes, PropsWithChildren } from 'react';
import styled from 'styled-components';

interface IData {
  marginLeft?: number;
  maxChar?: number;
  textAlign?: string;
}

export const TableWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    max-height: 60vh;
    overflow-y: auto;
    border-radius: 4px;
    border: 1px solid #eee;
    padding-bottom: 4px;
  }
`;

export const Table = styled.table`
  border-collapse: separate;
  border-spacing: 0 20px;
  width: 100%;
  min-width: 720px;

  @media (max-width: 1024px) {
    border-spacing: 0 10px;
  }

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

export const HeaderTable = styled.thead`
  box-shadow: 1px 1px 7px #ccc;
  border-radius: 4px;
  line-height: 30px;
  padding: 0.5rem;
  position: sticky;
  background: #fff;
  top: 0;
  z-index: 1;

  @media (max-width: 768px) {
    position: static;
    box-shadow: none;
  }
`;

export const HeaderRow = styled.tr`
  font-size: 16px;
`;

export const BodyTable = styled.tbody`
  line-height: 37px;
`;

const BodyRowTr = styled.tr`
  box-shadow: 0px 2px 2px -2px #ccc;
  background: #fff;
`;

export const BodyRow = ({
  children,
  ...props
}: PropsWithChildren<
  React.HTMLAttributes<HTMLTableRowElement>
>): JSX.Element => {
  return React.createElement(BodyRowTr, props, children);
};

export const CheckBox = styled.input.attrs({ type: 'checkbox' })<
  InputHTMLAttributes<HTMLInputElement>
>`
  appearance: none;
  background-color: #fff;
  margin: 0;
  font: inherit;
  color: #a1a1a1;
  width: 1.05em;
  height: 1.05em;
  border: 0.15em solid #a1a1a1;
  border-radius: 0.15em;
  transform: translateY(-0.075em);
  display: grid;
  place-content: center;

  &:before {
    content: '';
    width: 0.65em;
    height: 0.65em;
    transform: scale(0);
    transition: 120ms transform ease-in-out;
    box-shadow: inset 1em 1em #e71037;
    transform-origin: center center;
    clip-path: polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%);
  }
  &:checked:before {
    transform: scale(1);
  }
`;

const HeaderTitleCell = styled.th<IData>`
  font-size: 1.1em;
  font-weight: 600;
  padding: 3px 8px;
  text-align: ${(p) => p.textAlign || 'left'};
  color: #4a4b4a;
  letter-spacing: 0.02em;
  word-break: break-word;
  width: ${({ maxChar }) => (maxChar ? `${maxChar}ch` : '100%')};
  margin-left: ${({ marginLeft }) => (marginLeft ? `${marginLeft}px` : '0px')};
  line-height: 30px;
  font-size: 0.9rem;

  @media (max-width: 1024px) {
    font-size: 0.8rem;
    width: auto;
    padding: 6px 4px;
  }

  @media (max-width: 768px) {
    font-size: 0.75rem;
    width: auto;
    padding: 6px 4px;
  }
`;

export const HeaderTitle = ({
  children,
  ...props
}: PropsWithChildren<
  IData & React.ThHTMLAttributes<HTMLTableHeaderCellElement>
>): JSX.Element => {
  return React.createElement(HeaderTitleCell, props, children);
};

const BodyDataCell = styled.td<IData>`
  font-style: normal;
  font-weight: 350;
  font-size: 16px;
  color: #4a4b4a;
  padding: 4px 8px;

  white-space: nowrap;
  max-width: ${({ maxChar }) => (maxChar ? `${maxChar}ch` : '100%')};
  margin-left: ${({ marginLeft }) => (marginLeft ? `${marginLeft}px` : '0px')};
  text-align: ${({ textAlign }) => textAlign || 'left'};

  @media (max-width: 1024px) {
    font-size: 12px;
    max-width: 25ch;
    padding: 4px;
  }
`;

export const BodyData = ({
  children,
  ...props
}: PropsWithChildren<
  IData & React.TdHTMLAttributes<HTMLTableDataCellElement>
>): JSX.Element => {
  return React.createElement(BodyDataCell, props, children);
};

const BodyDataValueSpan = styled.span<IData>`
  max-width: ${({ maxChar }) => (maxChar ? `${maxChar}ch` : '100%')};
  white-space: nowrap;
  display: inline-block;
  text-overflow: ellipsis;
  overflow: hidden;
`;

export const BodyDataValue = ({
  children,
  ...props
}: PropsWithChildren<IData>): JSX.Element => {
  return React.createElement(BodyDataValueSpan, props, children);
};

const IconRowImage = styled.img`
  width: 20px;
  height: 20px;
  margin-left: 4px;

  @media (max-width: 1024px) {
    width: 16px;
    height: 16px;
  }
`;

export const IconRow = (
  props: React.ImgHTMLAttributes<HTMLImageElement>,
): JSX.Element => React.createElement(IconRowImage, props);

export const ButtonContainer = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
`;

interface IListRowBullet {
  color?: string;
}

export const ListRowBullet = styled.div<IListRowBullet>`
  background-color: ${(p) => (p.color ? p.color : 'transparent')};
  border-radius: 50px;
  height: 15px;
  width: 15px;
  margin-left: 3px;
`;
